import { COLORS, SPACING } from '@alto/design-library-tokens';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Fade } from '../../animations';
import { Border } from '../../borders';
import { Pressable } from '../../buttons';
import { Anchor, MdPadding, Row } from '../../containers';
import { Body } from '../../typography';
import { getBorderColor } from './AlertUtils';
import { type ColorVariant } from './types';

const getColors = (error?: boolean, warning?: boolean, success?: boolean) => {
  if (error) {
    return {
      background: COLORS.BACKGROUND_COLORS.DANGER_LIGHTEST,
      text: COLORS.TEXT_COLORS.DANGER,
      pressBackground: COLORS.BACKGROUND_COLORS.DANGER_LIGHTER,
    };
  }
  if (warning) {
    return {
      background: COLORS.BACKGROUND_COLORS.WARNING_LIGHTEST,
      text: COLORS.TEXT_COLORS.WARNING,
      pressBackground: COLORS.BACKGROUND_COLORS.WARNING_LIGHTER,
    };
  }
  if (success) {
    return {
      background: COLORS.BACKGROUND_COLORS.SUCCESS_LIGHTEST,
      text: COLORS.TEXT_COLORS.SUCCESS,
      pressBackground: COLORS.BACKGROUND_COLORS.SUCCESS_LIGHTER,
    };
  }
  return {
    background: COLORS.BACKGROUND_COLORS.PRIMARY_LIGHTEST,
    text: COLORS.TEXT_COLORS.BLACK,
    pressBackground: COLORS.BACKGROUND_COLORS.PRIMARY_LIGHTER,
  };
};

type Props = {
  readonly duration?: number;
  readonly delay?: number;
  readonly error?: boolean;
  readonly warning?: boolean;
  readonly success?: boolean;
  readonly children?: React.ReactNode;
  readonly ctaLabel?: string;
  readonly fullBleed?: boolean;
  readonly onPress?: () => void;
  readonly onHide?: () => void;
};

const isTest = !!process.env.JEST_WORKER_ID;

export const DEPRECATED_Toast = ({
  duration = 2000,
  delay,
  error,
  warning,
  success,
  children,
  ctaLabel,
  fullBleed,
  onPress,
  onHide,
}: Props) => {
  const [isVisible, setIsVisible] = useState(true);
  useEffect(() => {
    if (isTest) return;
    const timeout = setTimeout(() => {
      setIsVisible(false);
      onHide?.();
    }, duration);

    return () => {
      clearTimeout(timeout);
    };
  }, [onHide, duration, setIsVisible]);

  const color = getColors(error, warning, success);
  let type: ColorVariant = 'neutral';

  if (error) {
    type = 'error';
  }
  if (warning) {
    type = 'warning';
  }
  if (success) {
    type = 'success';
  }

  if (!isVisible) {
    return null;
  }

  return (
    <Anchor>
      <Fade
        duration={250}
        delay={delay}
      >
        <MdPadding
          leftPadding={fullBleed ? SPACING.STATIC.NONE : SPACING.STATIC.MD}
          rightPadding={fullBleed ? SPACING.STATIC.NONE : SPACING.STATIC.MD}
        >
          {onPress && !!ctaLabel ? (
            <Pressable
              onPress={onPress}
              backgroundColor={color.background}
              pressedBackgroundColor={color.pressBackground}
              opacity={false}
            >
              <Border color={getBorderColor(type)}>
                <Row
                  centerVertically
                  spaceBetween
                >
                  <Row flexShrink={1}>
                    <MdPadding rightPadding={SPACING.STATIC.NONE}>{children}</MdPadding>
                  </Row>
                  <Row>
                    <MdPadding>
                      <Body
                        fontFamily="bold"
                        color={color.text}
                        textTransform="capitalize"
                      >
                        {ctaLabel}
                      </Body>
                    </MdPadding>
                  </Row>
                </Row>
              </Border>
            </Pressable>
          ) : (
            <Border color={getBorderColor(type)}>
              <Row backgroundColor={color.background}>
                <MdPadding>{children}</MdPadding>
              </Row>
            </Border>
          )}
        </MdPadding>
      </Fade>
    </Anchor>
  );
};
